/**
 * InsightCard — proactive, contextual AI insights for a record page.
 * Fetches real insights from GET /api/copilot/insights/{doctype}/{recordId}
 * and renders them as compact, tone-colored tips. Renders nothing when empty.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Info, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import { copilotApi } from '../../services/copilotApi';

const TONE = {
  info: { icon: Info, box: 'bg-sky-50 border-sky-200', dot: 'text-sky-600', text: 'text-sky-900' },
  warning: { icon: AlertTriangle, box: 'bg-amber-50 border-amber-200', dot: 'text-amber-600', text: 'text-amber-900' },
  success: { icon: CheckCircle2, box: 'bg-green-50 border-green-200', dot: 'text-green-600', text: 'text-green-900' },
};

const InsightCard = ({ doctype, recordId }) => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    if (!doctype || !recordId) return undefined;
    setLoaded(false);
    copilotApi
      .insights(doctype, recordId)
      .then((res) => { if (active) { setInsights(res.data?.insights || []); setLoaded(true); } })
      .catch(() => { if (active) { setInsights([]); setLoaded(true); } });
    return () => { active = false; };
  }, [doctype, recordId]);

  if (!loaded || insights.length === 0) return null;

  return (
    <div
      className="mb-6 rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50/40 p-4"
      data-testid="insight-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-orange-600 text-white">
          <Sparkles className="h-3.5 w-3.5" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-orange-800">
          AI Insights
        </span>
      </div>
      <div className="space-y-2">
        {insights.map((tip, i) => {
          const tone = TONE[tip.tone] || TONE.info;
          const Icon = tone.icon;
          return (
            <div
              key={i}
              className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${tone.box}`}
              data-testid={`insight-item-${i}`}
            >
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone.dot}`} />
              <p className={`flex-1 text-sm ${tone.text}`}>{tip.text}</p>
              {tip.action?.path && (
                <button
                  type="button"
                  onClick={() => navigate(tip.action.path)}
                  className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-orange-700 hover:text-orange-900 hover:underline"
                  data-testid={`insight-action-${i}`}
                >
                  {tip.action.label || 'View'}
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InsightCard;
